
import React, { useEffect, useState, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { Underline } from '@tiptap/extension-underline';
import { deleteData, getData, patchData, postData } from "../../../services";
import { MessageCircle, User, Edit, Trash2, Bold, Italic, Underline as Underlines, List, ListOrdered, Code, Quote } from 'lucide-react';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { Heading } from '@tiptap/extension-heading'
import "./style.scss";
import DeleteModal from "../../organisms/modals/DeleteModal";
import { decodeCookieValue } from '../../../helpers/cookieHelper';

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="toolbar">
    <div className="toolbar-group">
      <button
        onClick={() => {
          editor.chain().focus().toggleHeading({ level: 1 }).run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`}
      >
        H1
      </button>
      <button
        onClick={() => {
          editor.chain().focus().toggleHeading({ level: 2 }).run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
      >
        H2
      </button>
      <button
        onClick={() => {
          editor.chain().focus().setParagraph().run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('paragraph') ? 'is-active' : ''}`}
      >
        Paragraph
      </button>
    </div>
    <div className="toolbar-group">
      <button
        onClick={() => {
          editor.chain().focus().toggleBold().run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('bold') ? 'is-active' : ''}`}
      >
        <Bold size={18} />
      </button>
      <button
        onClick={() => {
          editor.chain().focus().toggleItalic().run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('italic') ? 'is-active' : ''}`}
      >
        <Italic size={18} />
      </button>
      <button
        onClick={() => {
          editor.chain().focus().toggleUnderline().run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('underlines') ? 'is-active' : ''}`}
      >
        <Underlines size={18} />
      </button>
    </div>
    <div className="toolbar-group">
      <button
        onClick={() => {
          editor.chain().focus().toggleBulletList().run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('bulletList') ? 'is-active' : ''}`}
      >
        <List size={18} />
      </button>
      <button
        onClick={() => {
          editor.chain().focus().toggleOrderedList().run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('orderedList') ? 'is-active' : ''}`}
      >
        <ListOrdered size={18} />
      </button>
    </div>
    {/* <div className="toolbar-group">
      <button
        onClick={() => {
          editor.chain().focus().toggleCodeBlock().run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('codeBlock') ? 'is-active' : ''}`}
      >
        <Code size={18} />
      </button>
      <button
        onClick={() => {
          editor.chain().focus().toggleBlockquote().run();
          editor.commands.focus();
        }}
        className={`toolbar-button ${editor.isActive('blockquote') ? 'is-active' : ''}`}
      >
        <Quote size={18} />
      </button>
    </div> */}
  </div>
);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
};

const Comments = ({ tableData, relatedUsers }) => {
  const [cookies] = useCookies(['e_t', 'euid']);
  const [comments, setComments] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const commentsEndRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    commentId: null,
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Heading.configure({
        levels: [1, 2],
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: {
          items: ({ query }) => {
            return query.length === 0 
              ? relatedUsers.slice(0, 5) 
              : relatedUsers
                  .filter(user => user.name.toLowerCase().includes(query.toLowerCase()))
                  .slice(0, 5);
          },
          render: () => {
            let component;
            let popup;
      
            return {
              onStart: (props) => {
                component = document.createElement('div');
                component.classList.add('mention-suggestions');
                
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                  theme: 'light', 
                  arrow: false, 
                  offset: [0, 8], 
                });
                component.innerHTML = `
                  <div class="items">
                    ${props.items
                      .map(
                        (item) => `
                          <button class="item" data-user-id="${item.id}">
                            ${item.name}
                          </button>
                        `
                      )
                      .join('')}
                  </div>
                `;

                const buttons = component.querySelectorAll('button');
                buttons.forEach((button, index) => {
                  button.addEventListener('click', () => {
                    props.command({ id: button.dataset.userId, label: props.items[index].name });
                    popup[0].hide();
                  });
                });
              },
              onUpdate: (props) => {
                component.innerHTML = `
                  <div class="items">
                    ${props.items
                      .map(
                        (item) => `
                          <button class="item" data-user-id="${item.id}">
                            ${item.name}
                          </button>
                        `
                      )
                      .join('')}
                  </div>
                `;

                const buttons = component.querySelectorAll('button');
                buttons.forEach((button, index) => {
                  button.addEventListener('click', () => {
                    props.command({ id: button.dataset.userId, label: props.items[index].name });
                    popup[0].hide();
                  });
                });

                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                });
              },
              onKeyDown: (props) => {
                if (props.event.key === 'Escape') {
                  popup[0].hide();
                  return true;
                }
                return false;
              },
              onExit: () => {
                popup[0].destroy();
                component.remove();
              },
            };
          },
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
    },
  });

  const fetchComments = async () => {
    try {
      const res = await getData({
        endpoint: 'Comment/getComment',
        params: { task_id: tableData?.id || tableData?.task_id },
        token: cookies.e_t,
      });
      if (res) {
        setComments(res);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  // const handleSubmitComment = async () => {
  //   if (!editor?.getHTML() || editor.getHTML() === '<p></p>') return;

  //   // const content = editor.getHTML();
  //   // const mentions = Array.from(new DOMParser().parseFromString(content, 'text/html')
  //   //   .querySelectorAll('.mention'))
  //   //   .map(mention => ({
  //   //     user_id: mention.getAttribute('data-id') || '',
  //   //     name: mention.textContent?.replace('@', '').trim() || '',
  //   //   }));
  //   const content = editor.getHTML();
  //   const mentionedUserIds = [];
  // const domParser = new DOMParser();
  // const doc = domParser.parseFromString(content, 'text/html');
  
  // doc.querySelectorAll('.mention').forEach(mention => {
  //   const userId = mention.getAttribute('data-id');
  //   if (userId) {
  //     mentionedUserIds.push(userId); // Just push the ID string
  //   }
  // });
  

  //   if (editingCommentId) {
  //     try {
  //       const res = await patchData({
  //         endpoint: 'Comment/editComment',
  //         params: {
  //           comment_id: editingCommentId,
  //           comment: content,
  //         },
  //         data:{
  //           mentionedUserIds,
  //         },
  //         token: cookies.e_t,
  //       });

  //       if (res) {
  //         setComments(prev =>
  //           prev.map(comment =>
  //             comment.comment_id === editingCommentId
  //               ? {
  //                   ...comment,
  //                   comment: content,
  //                   tagged: mentionedUserIds,
  //                   time: new Date().toISOString(),
  //                 }
  //               : comment
  //           )
  //         );
  //       }
  //     } catch (error) {
  //       console.error('Error updating comment:', error);
  //       return;
  //     }
  //   } else {
  //     const optimisticComment = {
  //       comment_id: Date.now(),
  //       name: 'You',
  //       user_id: cookies.euid,
  //       comment: content,
  //       time: new Date().toISOString(),
  //       tagged: mentionedUserIds,
  //     };

  //     setComments(prev => [optimisticComment, ...prev]);

  //     try {
  //       const res = await postData({
  //         endpoint: 'Comment/newComment',
  //         params: {
  //           task_id: tableData?.id || tableData?.task_id,
  //           comment: content,
  //         },
  //         data: {
  //           mentionedUserIds
  //         },
  //         token: cookies.e_t,
  //       });

  //       if (res) {
  //         setComments(prev =>
  //           prev.map(comment =>
  //             comment.comment_id === optimisticComment.comment_id
  //               ? {
  //                   ...comment,
  //                   comment_id: res?.data?.data?.id,
  //                 }
  //               : comment
  //           )
  //         );
  //       }
  //     } catch (error) {
  //       console.error('Error submitting comment:', error);
  //       setComments(prev =>
  //         prev.filter(
  //           comment => comment.comment_id !== optimisticComment.comment_id
  //         )
  //       );
  //       return;
  //     }
  //   }

  //   editor.commands.setContent('');
  //   setEditingCommentId(null);
  // };
  
  const handleSubmitComment = async () => {
    if (!editor?.getHTML() || editor.getHTML() === '<p></p>') return;
  
    const content = editor.getHTML();
    
    // Extract mentions with both IDs and names
    const mentions = [];
    const domParser = new DOMParser();
    const doc = domParser.parseFromString(content, 'text/html');
    
    doc.querySelectorAll('.mention').forEach(mention => {
      const userId = mention.getAttribute('data-id');
      const userName = mention.textContent?.replace('@', '').trim();
      if (userId && userName) {
        mentions.push({
          user_id: userId,
          name: userName
        });
      }
    });
  
    // Prepare data for API - just the IDs
    const mentionedUserIds = mentions.map(m => m.user_id);
  
    if (editingCommentId) {
      try {
        const res = await patchData({
          endpoint: 'Comment/editComment',
          params: {
            comment_id: editingCommentId,
            comment: content,
          },
          data: mentionedUserIds, // Send just IDs to API
          headers: {
            'Content-Type': 'application/json'
          },
          token: cookies.e_t,
        });
  
        if (res) {
          setComments(prev =>
            prev.map(comment =>
              comment.comment_id === editingCommentId
                ? {
                    ...comment,
                    comment: content,
                    tagged: mentions, // Store full mention data locally
                    time: new Date().toISOString(),
                  }
                : comment
            )
          );
        }
      } catch (error) {
        console.error('Error updating comment:', error);
      }
    } else {
      const optimisticComment = {
        comment_id: Date.now(),
        name: decodeCookieValue(cookies.e_n),
        user_id: cookies.euid,
        comment: content,
        time: new Date().toISOString(),
        tagged: mentions, // Store full mention data locally
      };
  
      setComments(prev => [optimisticComment, ...prev]);
  
      try {
        const res = await postData({
          endpoint: 'Comment/newComment',
          params: {
            task_id: tableData?.id || tableData?.task_id,
            comment: content,
          },
          data: mentionedUserIds, // Send just IDs to API
          headers: {
            'Content-Type': 'application/json'
          },
          token: cookies.e_t,
        });
  
        if (res) {
          setComments(prev =>
            prev.map(comment =>
              comment.comment_id === optimisticComment.comment_id
                ? {
                    ...comment,
                    comment_id: res?.data?.data?.id,
                  }
                : comment
            )
          );
        }
      } catch (error) {
        console.error('Error submitting comment:', error);
        setComments(prev =>
          prev.filter(
            comment => comment.comment_id !== optimisticComment.comment_id
          )
        );
      }
    }
  
    editor.commands.setContent('');
    setEditingCommentId(null);
  };


  const handleEditComment = (commentId) => {
    const comment = comments.find(c => c.comment_id === commentId);
    if (comment) {
      editor?.commands.setContent(comment.comment);
      setEditingCommentId(commentId);
    }
  };

  const handleDeleteComment = (commentId) => {
    setDeleteModal({ isOpen: true, commentId });
  };

  const confirmDelete = async () => {
    if (!deleteModal.commentId) return;
    
    const originalComments = [...comments];
    setComments(prev =>
      prev.filter(comment => comment.comment_id !== deleteModal.commentId)
    );

    try {
      const res = await deleteData({
        endpoint: 'Comment/deleteComment',
        params: { comment_id: deleteModal.commentId },
        token: cookies.e_t,
      });

      if (!res) {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      setComments(originalComments);
    } finally {
      setDeleteModal({ isOpen: false, commentId: null });
    }
  };

  const handleCancel = () => {
    editor?.commands.setContent('');
    setEditingCommentId(null);
  };

  useEffect(() => {
    fetchComments();
  }, [tableData?.id, cookies.e_t]);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  return (
    <div className="comments-container">
    <div className="editor-container">
      <MenuBar editor={editor} />
      <div className="editor-content">
        <EditorContent editor={editor} />
      </div>
     
    </div>
     <div className="editor-footer">
        <div className="button-group">
          <button
            className="btn"
            onClick={handleCancel}
            disabled={!editor?.getHTML() || editor?.getHTML() === '<p></p>'}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmitComment}
            disabled={!editor?.getHTML() || editor?.getHTML() === '<p></p>'}
          >
            {editingCommentId ? 'Update' : 'Comment'}
          </button>
        </div>
      </div>

    {comments.length > 0 && (
      <div className="comments-header">
        <MessageCircle size={18} />
        <h5 className='mb-0'>Comments ({comments.length})</h5>
      </div>
    )}

    <div className="comments-list">
      {comments.map(comment => (
        <div key={comment.comment_id} className="comment-card">
          <div className="comment-header">
            <div className="user-info">
              <User size={18} />
              <div>
                <span className="user-name">{comment.name}</span>
                <span className="comment-time">{formatDate(comment.time)}</span>
              </div>
            </div>
            <div className="comment-actions">
              {/* {comment.tagged?.length > 0 && (
                <div className="tagged-users">
                  <span className="tagged-label">Tagged:</span>
                  {comment.tagged.map((user, index) => (
                    <span key={user.user_id} className="tagged-user">
                      {user.name}
                      {index < comment.tagged.length - 1 && ', '}
                    </span>
                  ))}
                </div>
              )} */}
              {cookies.euid === comment?.user_id && (
                <div className="action-buttons">
                  <Edit className="edit-btn" onClick={() => handleEditComment(comment.comment_id)} />
                  <Trash2 className="delete-btn" onClick={() => handleDeleteComment(comment.comment_id)} />
                </div>
              )}
            </div>
          </div>
          <div 
            className="comment-content"
            dangerouslySetInnerHTML={{ __html: comment.comment }}
          />
        </div>
      ))}
      <div ref={commentsEndRef} />
    </div>
      <DeleteModal
        isActive={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, commentId: null })}
        title="Comment"
        onClick={confirmDelete}
      />
    </div>
  );
};

export default Comments;